/**
 * Staff Settings Page - Olympic Fit Wellness
 * Connected to live Supabase backend
 */
import { useState, useEffect } from 'react';
import { 
  User, 
  Bell,
  Calendar,
  Save,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function StaffSettingsPage() {
  const { profile, user, refreshProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    bio: '',
    specialties: ''
  });

  useEffect(() => {
    if (profile || user) {
      setFormData({
        full_name: profile?.full_name || '',
        email: user?.email || '',
        phone: profile?.phone || '',
        bio: profile?.bio || '',
        specialties: profile?.specialties || ''
      });
    }
  }, [profile, user]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await api.updateMyProfile({
        full_name: formData.full_name,
        phone: formData.phone
      });
      setSaveSuccess(true);
      if (refreshProfile) {
        await refreshProfile();
      }
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-settings">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-neutral-900">Settings</h1>
        <p className="text-neutral-500">Manage your profile and preferences</p>
      </div>

      {/* Profile Settings */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-neutral-100">
          <h2 className="text-xl font-heading font-bold text-neutral-900 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Specialties</label>
              <input
                type="text"
                value={formData.specialties}
                onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                placeholder="e.g., HIIT, Strength Training, Yoga"
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              rows={4}
              placeholder="Tell clients about yourself..."
              className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-green-500 resize-none"
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-neutral-100 flex justify-end gap-4">
          {saveSuccess && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span>Saved successfully!</span>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 bg-green-600 text-white font-semibold px-6 py-3 text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4" /> Save Changes</>
            )}
          </button>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-neutral-100">
          <h2 className="text-xl font-heading font-bold text-neutral-900 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </h2>
        </div>
        
        <div className="p-6 space-y-4">
          {[
            { key: 'new_bookings', title: 'New Bookings', desc: 'Get notified when clients book sessions' },
            { key: 'cancellations', title: 'Cancellations', desc: 'Get notified when clients cancel' },
            { key: 'schedule_changes', title: 'Schedule Changes', desc: 'Get notified about schedule updates' },
            { key: 'client_messages', title: 'Client Messages', desc: 'Get notified about client messages' }
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div>
                <h4 className="font-medium text-neutral-900">{item.title}</h4>
                <p className="text-sm text-neutral-500">{item.desc}</p>
              </div>
              <button className="w-12 h-6 rounded-full bg-green-600 transition-colors">
                <div className="w-5 h-5 bg-white rounded-full shadow translate-x-6" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Availability Settings */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-neutral-100">
          <h2 className="text-xl font-heading font-bold text-neutral-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Availability
          </h2>
        </div>
        
        <div className="p-6">
          <p className="text-neutral-500 mb-4">Set your weekly availability for personal training sessions.</p>
          <button className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">
            Manage Availability
          </button>
        </div>
      </div>
    </div>
  );
}
